import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import Pagination from "../../Helpers/Pagination";
import dayjs from "dayjs";
import { Modal, ModalBody, Row, Col, Button } from "reactstrap";
import { LocalizationProvider, DatePicker, TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Select from "react-select";

const AttendanceList = () => {
    const dispatch = useDispatch();

    const [data, setData] = useState([]);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [editModal, setEditModal] = useState(false);
    const [editItem, setEditItem] = useState(null);

    // Default-ah today set panniduren, appo thaan confusing-ah irukathu
    const [fromDate, setFromDate] = useState(dayjs());
    const [toDate, setToDate] = useState(dayjs());

    const limit = 20;

    const statusOptions = [
        { value: "present", label: "Present" },
        { value: "absent", label: "Absent" },
        { value: "leave", label: "Leave" },
        { value: "mc", label: "MC" }
    ];

    const loadAttendance = async () => {
        dispatch(loadingTrue());

        const offset = (page - 1) * limit;
        // YYYY-MM-DD format-la API-ku anupurean
        const fDate = fromDate ? fromDate.format("YYYY-MM-DD") : "";
        const tDate = toDate ? toDate.format("YYYY-MM-DD") : "";

        let url = `/attendance/list.php?limit=${limit}&offset=${offset}&from_date=${fDate}&to_date=${tDate}`;

        const res = await Handler({ method: "get", url });
        if (res?.success) {
            setData(res?.data);
            setTotal(res.total_count);
        }

        dispatch(loadingFalse());
    };

    const openEdit = (item) => {
        setEditItem({
            ...item,
            attendance_date: dayjs(item.attendance_date),
            check_in: item.check_in ? dayjs(`2024-01-01 ${item.check_in}`) : null,
            check_out: item.check_out ? dayjs(`2024-01-01 ${item.check_out}`) : null,
            status: statusOptions?.find(s => s.value === item.status)
        });
        setEditModal(true);
    };

    const updateAttendance = async () => {
        dispatch(loadingTrue());
        const payload = {
            id: editItem.id,
            driver_id: editItem.driver_id,
            attendance_date: editItem.attendance_date.format("YYYY-MM-DD"),
            status: editItem.status.value,
            check_in: editItem.check_in ? editItem.check_in.format("HH:mm:ss") : null,
            check_out: editItem.check_out ? editItem.check_out.format("HH:mm:ss") : null,
            remarks: editItem.remarks || ""
        };

        const res = await Handler({
            method: "post",
            url: "/attendance/update.php",
            data: payload
        });

        if (res?.status) {
            setEditModal(false);
            loadAttendance();
        } else {
            alert(res?.message || "Update failed");
        }
        dispatch(loadingFalse());
    };

    useEffect(() => { loadAttendance(); }, [page, fromDate, toDate]);

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="page-card p-4">
                {/* HEADER WITH MUI FILTERS */}
                <div className="page-header mb-4 d-flex align-items-center justify-content-between">
                    <h4 className="fw-bold">Driver Attendance</h4>

                    <div className="d-flex gap-3 align-items-center bg-light p-2 rounded-3 shadow-sm">
                        <DatePicker
                            label="From Date"
                            slotProps={{ textField: { size: 'small', variant: 'outlined', sx: { width: 160 } } }}
                            value={fromDate}
                            onChange={(val) => { setPage(1); setFromDate(val); }}
                        />
                        <DatePicker
                            label="To Date"
                            slotProps={{ textField: { size: 'small', variant: 'outlined', sx: { width: 160 } } }}
                            value={toDate}
                            onChange={(val) => { setPage(1); setToDate(val); }}
                        />
                        <Button 
                            color="secondary" 
                            size="sm" 
                            className="px-3"
                            onClick={() => {
                                setFromDate(dayjs());
                                setToDate(dayjs());
                            }}
                        >
                            Today
                        </Button>
                    </div>
                </div>

                {/* TABLE */}
                <div className="table-wrap shadow-sm rounded-3 overflow-hidden">
                    <table className="table modern-table mb-0">
                        <thead className="bg-primary text-white">
                            <tr>
                                <th>Date</th>
                                <th>Driver</th>
                                <th>Status</th>
                                <th>Check In</th>
                                <th>Check Out</th>
                                <th>Total Hours</th>
                                <th>Remarks</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data?.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="text-center py-5 text-muted">
                                        No attendance records found for this period
                                    </td>
                                </tr>
                            ) : data.map(row => (
                                <tr key={row.id}>
                                    <td>{dayjs(row.attendance_date).format("DD-MM-YYYY")}</td>
                                    <td className="fw-semibold text-primary">{row.driver_name}</td>
                                    <td>
                                        <span className={`status-badge ${row.status}`}>
                                            {row.status}
                                        </span>
                                    </td>
                                    <td>{row.check_in ? dayjs(`2024-01-01 ${row.check_in}`).format("hh:mm A") : "--"}</td>
                                    <td>{row.check_out ? dayjs(`2024-01-01 ${row.check_out}`).format("hh:mm A") : "--"}</td>
                                    <td className="fw-bold">{row.total_hours ?? "--"}</td>
                                    <td className="text-muted small">{row.remarks || "--"}</td>
                                    <td>
                                        <button className="icon-btn edit text-primary" onClick={() => openEdit(row)}>
                                            <i className='bx bx-edit fs-5'></i>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* PAGINATION */}
                <div className="mt-4">
                    <Pagination
                        page={page}
                        total={total}
                        limit={limit}
                        onPrev={() => setPage(p => Math.max(1, p - 1))}
                        onNext={() => setPage(p => p + 1)}
                    />
                </div>

                {/* EDIT MODAL */}
                <Modal isOpen={editModal} toggle={() => setEditModal(false)} centered>
                    <ModalBody className="p-4">
                        <div className="attendance-header d-flex align-items-center mb-4 border-bottom pb-3">
                            <div className="icon-wrap bg-primary-subtle text-primary p-3 rounded-circle me-3">
                                <i className='bx bx-user-check fs-3'></i>
                            </div>
                            <div>
                                <h5 className="mb-0 fw-bold">Update Attendance</h5>
                                <span className="text-muted small">{editItem?.driver_name}</span>
                            </div>
                        </div>

                        {editItem && (
                            <div className="attendance-form d-flex flex-column gap-3">
                                <DatePicker
                                    label="Attendance Date"
                                    value={editItem.attendance_date}
                                    onChange={(val) => setEditItem({ ...editItem, attendance_date: val })}
                                    maxDate={dayjs()}
                                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                />

                                <div>
                                    <label className="form-label small fw-bold text-secondary">Attendance Status</label>
                                    <Select
                                        options={statusOptions}
                                        value={editItem?.status}
                                        onChange={(val) => setEditItem({ ...editItem, status: val })}
                                        isSearchable={false}
                                    />
                                </div>

                                {editItem.status.value === "present" && (
                                    <Row className="g-2">
                                        <Col md={6}>
                                            <TimePicker
                                                label="Check In"
                                                value={editItem.check_in}
                                                onChange={(val) => setEditItem({ ...editItem, check_in: val })}
                                                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <TimePicker
                                                label="Check Out"
                                                value={editItem.check_out}
                                                onChange={(val) => setEditItem({ ...editItem, check_out: val })}
                                                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                            />
                                        </Col>
                                    </Row>
                                )}

                                <div>
                                    <label className="form-label small fw-bold text-secondary">Remarks</label>
                                    <textarea
                                        className="form-control"
                                        rows="3"
                                        value={editItem.remarks || ""}
                                        onChange={(e) => setEditItem({ ...editItem, remarks: e.target.value })}
                                        placeholder="Add any internal notes..."
                                    />
                                </div>
                            </div>
                        )}

                        <div className="d-flex gap-2 mt-4">
                            <Button color="light" className="w-100 fw-bold" onClick={() => setEditModal(false)}>Cancel</Button>
                            <Button color="primary" className="w-100 fw-bold shadow-sm" onClick={updateAttendance}>Save Changes</Button>
                        </div>
                    </ModalBody>
                </Modal>
            </div>
        </LocalizationProvider>
    );
};

export default AttendanceList;